import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { Stars, OrbitControls } from "@react-three/drei";
import { Constellation } from "@chuzi/shared/realms/cosmos/components";
import { WildsSandbox } from "@chuzi/shared/realms/wilds/components";
import { cosmosMapping } from "@chuzi/shared/realms/cosmos";
import { distributeStars } from "@chuzi/shared/realms/cosmos/components";
import { ChuziApiError } from "@chuzi/shared/api";
import type {
  CatalogResponse,
  StoryListItem,
  StoryProgress,
  UserProfile,
  RealmId,
} from "@chuzi/shared/types";
import { api } from "../lib/api";
import { auth } from "../lib/auth";
import { beginOidcLogout } from "../lib/oidc";
import { useCloseAllModalsListener } from "../lib/close-all-modals";
import {
  DirectorMenu,
  type DirectorMenuAction,
} from "../components/director-menu";
import { DirectorProfileModal } from "../components/director-profile-modal";
import { StoryHoverHud } from "../components/story-hover-hud";
import { CreateInline } from "../components/create-inline";
import { CreateFab } from "../components/create-fab";
import { StoryPicker } from "../components/story-picker";
import { StarBirth } from "../components/star-birth";
import { TreeSprout } from "../components/tree-sprout";
import { SceneOrbits } from "../components/scene-orbits";
import { SceneBranches } from "../components/scene-branches";
import { ForestBackdrop } from "@chuzi/shared/realms/wilds/components";
import * as THREE from "three";

type CreatingState =
  | { phase: "idle" }
  | { phase: "animating" }
  | { phase: "form" }
  | { phase: "scenes"; storyId: string };

type PlacedStory = {
  story: StoryListItem;
  visual: ReturnType<typeof cosmosMapping> & { position: [number, number, number] };
  dimmed: boolean;
};

export function CatalogPage() {
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [openModal, setOpenModal] = useState<"profile" | null>(null);
  const [creating, setCreating] = useState<CreatingState>({ phase: "idle" });
  const [hudScreen, setHudScreen] = useState<{ x: number; y: number } | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef<NavigateFunction>(navigate);
  navigateRef.current = navigate;

  const films = catalog?.data ?? null;
  const realm: RealmId = user?.realm === "wilds" ? "wilds" : "cosmos";
  const isCreating = creating.phase !== "idle";

  useCloseAllModalsListener(
    useCallback(() => {
      setOpenModal(null);
    }, []),
  );

  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigateRef.current("/login", { replace: true });
      return;
    }
    let cancelled = false;
    api.catalog
      .index()
      .then((res) => {
        if (cancelled) return;
        setCatalog(res);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ChuziApiError && err.status === 401) {
          auth.clearToken();
          navigateRef.current("/login", { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load catalog");
      });
    api.auth
      .user()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const progress = catalog?.meta.progress ?? {};

  const placed: PlacedStory[] = useMemo(() => {
    if (!films || films.length === 0) return [];
    const positions = distributeStars(films.length, { seed: 1, radius: 18 });
    return films.map((film, i) => {
      const isOwn = !!(user && film.creator && film.creator.id === user.id);
      const prog = progress[film.id] as StoryProgress | undefined;
      const hasWatched = !!(prog && prog.last_scene_id);
      return {
        story: film,
        visual: { ...cosmosMapping(film), position: positions[i] },
        dimmed: !isOwn && !hasWatched,
      };
    });
  }, [films, progress, user]);

  const focusedItem = placed.length > 0 && focusedIndex < placed.length
    ? placed[focusedIndex]
    : null;

  useEffect(() => {
    if (!isCreating && placed.length > 0) {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(placed.length - 1, prev + 1));
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(0, prev - 1));
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
  }, [isCreating, placed.length]);

  function logout() {
    api.auth
      .logout()
      .catch(() => {})
      .finally(() => {
        auth.clearToken();
        beginOidcLogout();
      });
  }

  function handleMenuAction(action: DirectorMenuAction) {
    if (action === "logout") {
      logout();
      return;
    }
    if (action === "profile") {
      setOpenModal("profile");
    }
  }

  function handleCreateClick() {
    setCreating({ phase: "animating" });
  }

  function handleBirthSettled() {
    setCreating({ phase: "form" });
  }

  function handleCreated(storyId: string) {
    setCreating({ phase: "scenes", storyId });
    setTimeout(() => {
      navigate("/my", { replace: true });
    }, 3000);
  }

  function handleCreateCancel() {
    setCreating({ phase: "idle" });
  }

  const showHud = !isCreating && focusedItem != null && hudScreen != null;

  const placedScene = focusedItem
    ? {
        id: focusedItem.story.id,
        story_id: focusedItem.story.id,
        title: focusedItem.story.title,
        order: 0,
        is_title: true as const,
        is_end: false as const,
        color: null,
        media_id: null,
        alt_media_id: null,
        media_mode: null,
        text_content: null,
        text_style: null,
        created_at: focusedItem.story.created_at,
        updated_at: focusedItem.story.updated_at,
      }
    : null;

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh", background: "#04070d" }}>
      {isCreating ? (
        realm === "wilds" ? (
          <CreationCanvasWilds creating={creating} onBirthSettled={handleBirthSettled} />
        ) : (
          <CreationCanvasCosmos creating={creating} onBirthSettled={handleBirthSettled} />
        )
      ) : films ? (
        realm === "wilds" ? (
          <WildsSandbox films={films} onFilmSelect={(f) => {
            const idx = placed.findIndex((p) => p.story.id === f.id);
            if (idx >= 0) setFocusedIndex(idx);
          }} />
        ) : (
          <CatalogCosmosScene
            placed={placed}
            focusedIndex={focusedIndex}
            onHudProject={setHudScreen}
            onSelect={(i) => setFocusedIndex(i)}
          />
        )
      ) : error ? (
        <CenterMessage tone="error">{error}</CenterMessage>
      ) : (
        <CenterMessage>
          {realm === "wilds" ? "Loading the wilds…" : "Loading the cosmos…"}
        </CenterMessage>
      )}

      <DirectorMenu user={user} onAction={handleMenuAction} />

      {!isCreating ? (
        <CreateFab realm={realm} onClick={handleCreateClick} />
      ) : null}

      {films && films.length > 1 && !isCreating ? (
        <StoryPicker
          stories={films}
          selectedIndex={focusedIndex}
          onSelect={setFocusedIndex}
          realm={realm}
        />
      ) : null}

      {showHud && focusedItem && placedScene ? (
        <StoryHoverHud
          story={focusedItem.story}
          scene={placedScene}
          screenX={hudScreen.x}
          screenY={hudScreen.y}
          realm={realm}
          coverboxUrl={catalog?.meta.coverboxes[focusedItem.story.id] ?? null}
          creatorAvatarUrl={
            focusedItem.story.creator
              ? (catalog?.meta.creator_avatars[focusedItem.story.creator.id] ?? null)
              : null
          }
        />
      ) : null}

      {creating.phase === "form" ? (
        <CreateInline
          realm={realm}
          onCreated={handleCreated}
          onCancel={handleCreateCancel}
        />
      ) : null}

      {creating.phase === "animating" ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "rgba(232,240,255,0.5)",
            fontSize: 12,
            letterSpacing: 1,
            textTransform: "uppercase",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {realm === "wilds" ? "Planting…" : "Igniting…"}
        </div>
      ) : null}

      {openModal === "profile" ? (
        <DirectorProfileModal
          user={user}
          onClose={() => setOpenModal(null)}
          onUserUpdated={(u) => setUser(u)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Catalog cosmos 3D scene                                            */
/* ------------------------------------------------------------------ */

function CatalogCosmosScene({
  placed,
  focusedIndex,
  onHudProject,
  onSelect,
}: {
  placed: PlacedStory[];
  focusedIndex: number;
  onHudProject: (pos: { x: number; y: number } | null) => void;
  onSelect: (index: number) => void;
}) {
  return (
    <Canvas
      camera={{ position: [0, 6, 20], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <color attach="background" args={["#04070d"]} />
      <ambientLight intensity={0.15} />
      <Stars
        radius={120}
        depth={60}
        count={3000}
        factor={4}
        fade
        saturation={0.4}
      />

      {placed.map(({ story, visual, dimmed }, i) => (
        <Constellation
          key={`constellation-${story.id}`}
          scenes={[{ id: story.id, visual, dimmed }]}
          onSceneSelect={() => onSelect(i)}
        />
      ))}

      {placed.length > 0 && focusedIndex < placed.length ? (
        <FocusRing
          position={placed[focusedIndex].visual.position}
          color="#7eb8ff"
        />
      ) : null}

      <CatalogCameraController
        placed={placed}
        focusedIndex={focusedIndex}
        onHudProject={onHudProject}
      />
    </Canvas>
  );
}

/* ------------------------------------------------------------------ */
/*  Catalog camera controller                                          */
/* ------------------------------------------------------------------ */

function CatalogCameraController({
  placed,
  focusedIndex,
  onHudProject,
}: {
  placed: PlacedStory[];
  focusedIndex: number;
  onHudProject: (pos: { x: number; y: number } | null) => void;
}) {
  const { camera, size } = useThree();
  const targetPos = useRef(new THREE.Vector3(0, 4, 16));
  const targetLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const mode = useRef<"transitioning" | "free">("transitioning");
  const controlsRef = useRef<any>(null);
  const prevFocusedIndex = useRef(focusedIndex);

  if (focusedIndex !== prevFocusedIndex.current) {
    mode.current = "transitioning";
    prevFocusedIndex.current = focusedIndex;
  }

  useFrame(() => {
    let goalPos: THREE.Vector3;
    let goalLookAt: THREE.Vector3;

    if (placed.length > 0 && focusedIndex < placed.length) {
      const p = placed[focusedIndex].visual.position;
      goalLookAt = new THREE.Vector3(p[0], p[1], p[2]);
      goalPos = new THREE.Vector3(p[0], p[1] + 4, p[2] + 12);

      const projected = new THREE.Vector3(p[0], p[1] + 0.5, p[2]);
      projected.project(camera);
      const sx = (projected.x * 0.5 + 0.5) * size.width;
      const sy = (-projected.y * 0.5 + 0.5) * size.height;
      onHudProject({ x: sx, y: sy });
    } else {
      goalPos = new THREE.Vector3(0, 6, 20);
      goalLookAt = new THREE.Vector3(0, 0, 0);
      onHudProject(null);
    }

    if (mode.current === "transitioning") {
      targetPos.current.lerp(goalPos, 0.06);
      targetLookAt.current.lerp(goalLookAt, 0.06);
      camera.position.copy(targetPos.current);
      camera.lookAt(targetLookAt.current);

      if (targetPos.current.distanceTo(goalPos) < 0.05) {
        mode.current = "free";
        if (controlsRef.current) {
          controlsRef.current.target.copy(goalLookAt);
          controlsRef.current.update();
        }
      }
    } else {
      targetPos.current.copy(camera.position);
      targetLookAt.current.copy(
        controlsRef.current ? controlsRef.current.target : goalLookAt,
      );

      if (placed.length > 0 && focusedIndex < placed.length) {
        const p = placed[focusedIndex].visual.position;
        const projected = new THREE.Vector3(p[0], p[1] + 0.5, p[2]);
        projected.project(camera);
        const sx = (projected.x * 0.5 + 0.5) * size.width;
        const sy = (-projected.y * 0.5 + 0.5) * size.height;
        onHudProject({ x: sx, y: sy });
      }
    }
  });

  const isFree = mode.current === "free" && placed.length > 0;

  return (
    <OrbitControls
      ref={controlsRef}
      enabled={isFree}
      enableDamping
      dampingFactor={0.08}
      minDistance={2}
      maxDistance={50}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Focus ring                                                         */
/* ------------------------------------------------------------------ */

function FocusRing({
  position,
  color,
}: {
  position: [number, number, number];
  color: string;
}) {
  const ref = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const pulse = 1 + Math.sin(clock.elapsedTime * 2) * 0.15;
    ref.current.scale.set(pulse, 1, pulse);
  });

  return (
    <mesh
      ref={ref}
      position={[position[0], position[1] - 0.3, position[2]]}
      rotation={[-Math.PI / 2, 0, 0]}
    >
      <ringGeometry args={[0.7, 0.85, 32]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.35}
        toneMapped={false}
      />
    </mesh>
  );
}

/* ------------------------------------------------------------------ */
/*  Creation canvases                                                   */
/* ------------------------------------------------------------------ */

interface CreationCanvasProps {
  creating: CreatingState;
  onBirthSettled: () => void;
}

function CreationCanvasCosmos({ creating, onBirthSettled }: CreationCanvasProps) {
  return (
    <Canvas
      camera={{ position: [0, 4, 12], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <color attach="background" args={["#04070d"]} />
      <ambientLight intensity={0.15} />
      <Stars
        radius={120}
        depth={60}
        count={3000}
        factor={4}
        fade
        saturation={0.4}
      />

      {(creating.phase === "animating" || creating.phase === "form") ? (
        <StarBirth position={[0, 0, 0]} hue={210} onSettled={onBirthSettled} />
      ) : null}

      {creating.phase === "scenes" ? (
        <SceneOrbits center={[0, 0, 0]} hue={210} />
      ) : null}
    </Canvas>
  );
}

function CreationCanvasWilds({ creating, onBirthSettled }: CreationCanvasProps) {
  return (
    <Canvas
      camera={{ position: [0, 8, 14], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <color attach="background" args={["#0e1030"]} />
      <fog attach="fog" args={["#0e1030", 70, 180]} />
      <Stars
        radius={180}
        depth={80}
        count={2500}
        factor={4}
        saturation={0.5}
        fade
        speed={0.8}
      />
      <ambientLight intensity={0.65} />
      <hemisphereLight args={["#f0f8ff", "#3a5a38", 1.2]} />
      <directionalLight position={[10, 30, 8]} intensity={1.5} color="#fff5e0" />
      <WildsGround />
      <ForestBackdrop count={200} innerRadius={28} outerRadius={70} />

      {(creating.phase === "animating" || creating.phase === "form") ? (
        <TreeSprout position={[0, 0, 0]} hue={110} onSettled={onBirthSettled} />
      ) : null}

      {creating.phase === "scenes" ? (
        <SceneBranches center={[0, 0, 0]} hue={110} />
      ) : null}
    </Canvas>
  );
}

function WildsGround() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
      <circleGeometry args={[120, 64]} />
      <meshStandardMaterial color="#2a5a35" roughness={0.9} />
    </mesh>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                      */
/* ------------------------------------------------------------------ */

function CenterMessage({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "error";
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: tone === "error" ? "#ff8888" : "rgba(232,240,255,0.65)",
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}
